/* Nordic Pet House: shared, conservative product calculations and selection. */
(() => {
  'use strict';
  const normalize = value => String(value || '').toLowerCase().replace(/,/g, '.');

  function packageGrams(value) {
    const text = normalize(value);
    const multipack = [...text.matchAll(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(kg|g)\b/g)].pop();
    if (multipack) return Number(multipack[1]) * Number(multipack[2]) * (multipack[3] === 'kg' ? 1000 : 1);
    const match = [...text.matchAll(/(\d+(?:\.\d+)?)\s*(kg|g)\b/g)].pop();
    return match ? Number(match[1]) * (match[2] === 'kg' ? 1000 : 1) : null;
  }

  function netGrams(variant, title, allowTitle = true) {
    const measure = variant?.unit_price_measurement;
    if (measure?.quantity_unit === 'kg' && Number(measure.quantity_value) > 0) return Number(measure.quantity_value) * 1000;
    if (measure?.quantity_unit === 'g' && Number(measure.quantity_value) > 0) return Number(measure.quantity_value);
    // Shopify variant.weight/grams is shipping weight, not necessarily net food weight.
    return packageGrams(variant?.title) || (allowTitle ? packageGrams(title) : null);
  }

  function assess(product, values) {
    const facts = product.facts || {};
    if (!product.available || Number(product.price_cents) <= 500) return null;
    if (facts.animal !== values.animal && facts.animal !== 'both') return null;
    if (!['dry', 'wet'].includes(facts.format)) return null;
    if (values.format !== 'both' && values.format !== 'any' && facts.format !== values.format) return null;
    if (facts.stage !== 'all' && facts.stage !== values.stage) return null;
    if (facts.feeding_role === 'complementary') return null;
    if (facts.size && facts.size !== 'all' && facts.size !== values.size) return null;
    // A young large-breed dog needs an explicitly suitable recipe, not a generic guess.
    if (values.animal === 'dog' && values.stage === 'young' && values.size === 'large' && facts.size !== 'large') return null;
    if (values.tolerance === 'single' && facts.single_protein !== true) return null;
    const reasons = [values.animal === 'cat' ? 'För katt' : 'För hund', facts.format === 'wet' ? 'Våtfoder' : 'Torrfoder'];
    const stageLabels = { all: 'Alla livsstadier', young: values.animal === 'cat' ? 'Kattunge' : 'Valp', adult: 'Vuxen', senior: 'Senior' };
    reasons.push(stageLabels[facts.stage]);
    let score = facts.stage === values.stage ? 5 : 3;
    if (facts.feeding_role === 'complete') { score += 2; reasons.push('Angivet som helfoder'); }
    if (facts.size === values.size) { score += 2; reasons.push('Angiven storleksklass stämmer'); }
    if (facts.protein && facts.protein === values.protein) { score += 2; reasons.push('Angiven proteinkälla stämmer'); }
    if (facts.single_protein === true && values.tolerance === 'single') reasons.push('Angivet singelprotein');
    const notes = [];
    if (facts.feeding_role !== 'complete') notes.push('Kontrollera på förpackningen om det är helfoder eller kompletteringsfoder.');
    if (values.protein && !['any', 'none'].includes(values.protein) && facts.protein !== values.protein) notes.push('Din önskade proteinkälla är inte bekräftad i våra strukturerade uppgifter.');
    if (values.focus === 'sensitive' || values.tolerance === 'gentle' || values.tolerance === 'single') notes.push('Matchningen bedömer inte allergi eller medicinska behov. Kontrollera hela ingredienslistan och fråga veterinär vid besvär.');
    if (values.priority === 'balanced') {
      const grams = netGrams({ title: product.variant_title, unit_price_measurement: product.unit_price_measurement }, product.title, product.variant_count === 1);
      if (grams > 0) score += 1 / (1 + Number(product.price_cents) / grams);
    }
    return { product, score, reason: `${reasons.join(' · ')}. ${notes.join(' ')}`.trim(), label: 'Utifrån produktuppgifter' };
  }

  let catalogPromise;
  function catalog() {
    if (!catalogPromise) catalogPromise = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const products = [];
      let page = 1;
      do {
        const url = new URL(`${root}collections/all`, window.location.origin);
        url.searchParams.set('view', 'npc-match-data');
        url.searchParams.set('page', String(page));
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        let html;
        try {
          const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
          if (!response.ok) throw new Error('Sortimentet kunde inte laddas.');
          html = await response.text();
        } finally {
          window.clearTimeout(timeout);
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const payload = JSON.parse(parsed.querySelector('[data-npc-match-catalog]')?.textContent || 'null');
        if (payload?.version !== 1 || !Array.isArray(payload.products)) throw new Error('Produktuppgifterna kunde inte läsas.');
        products.push(...payload.products);
        if (!payload.next_page) break;
        if (payload.next_page !== page + 1 || page >= 100) throw new Error('Sortimentet kunde inte läsas färdigt.');
        page = payload.next_page;
      } while (true);
      return products;
    })().catch(error => { catalogPromise = undefined; throw error; });
    return catalogPromise;
  }

  window.NPCProductCore = Object.freeze({ packageGrams, netGrams, assess, catalog });
})();
